import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { collection, addDoc, doc, getDoc, updateDoc, increment, Timestamp } from 'firebase/firestore';
import { db } from '../firebase/firebaseConfig';
import { useCart } from '../context/CartContext';
import { useAuth } from '../context/AuthContext';

const TAX_RATE = 0.0825;

// All 50 US states as abbreviation + full name pairs
const US_STATES = [
  { abbr: 'AL', name: 'Alabama' },       { abbr: 'AK', name: 'Alaska' },
  { abbr: 'AZ', name: 'Arizona' },       { abbr: 'AR', name: 'Arkansas' },
  { abbr: 'CA', name: 'California' },    { abbr: 'CO', name: 'Colorado' },
  { abbr: 'CT', name: 'Connecticut' },   { abbr: 'DE', name: 'Delaware' },
  { abbr: 'FL', name: 'Florida' },       { abbr: 'GA', name: 'Georgia' },
  { abbr: 'HI', name: 'Hawaii' },        { abbr: 'ID', name: 'Idaho' },
  { abbr: 'IL', name: 'Illinois' },      { abbr: 'IN', name: 'Indiana' },
  { abbr: 'IA', name: 'Iowa' },          { abbr: 'KS', name: 'Kansas' },
  { abbr: 'KY', name: 'Kentucky' },      { abbr: 'LA', name: 'Louisiana' },
  { abbr: 'ME', name: 'Maine' },         { abbr: 'MD', name: 'Maryland' },
  { abbr: 'MA', name: 'Massachusetts' }, { abbr: 'MI', name: 'Michigan' },
  { abbr: 'MN', name: 'Minnesota' },     { abbr: 'MS', name: 'Mississippi' },
  { abbr: 'MO', name: 'Missouri' },      { abbr: 'MT', name: 'Montana' },
  { abbr: 'NE', name: 'Nebraska' },      { abbr: 'NV', name: 'Nevada' },
  { abbr: 'NH', name: 'New Hampshire' }, { abbr: 'NJ', name: 'New Jersey' },
  { abbr: 'NM', name: 'New Mexico' },    { abbr: 'NY', name: 'New York' },
  { abbr: 'NC', name: 'North Carolina' },{ abbr: 'ND', name: 'North Dakota' },
  { abbr: 'OH', name: 'Ohio' },          { abbr: 'OK', name: 'Oklahoma' },
  { abbr: 'OR', name: 'Oregon' },        { abbr: 'PA', name: 'Pennsylvania' },
  { abbr: 'RI', name: 'Rhode Island' },  { abbr: 'SC', name: 'South Carolina' },
  { abbr: 'SD', name: 'South Dakota' },  { abbr: 'TN', name: 'Tennessee' },
  { abbr: 'TX', name: 'Texas' },         { abbr: 'UT', name: 'Utah' },
  { abbr: 'VT', name: 'Vermont' },       { abbr: 'VA', name: 'Virginia' },
  { abbr: 'WA', name: 'Washington' },    { abbr: 'WV', name: 'West Virginia' },
  { abbr: 'WI', name: 'Wisconsin' },     { abbr: 'WY', name: 'Wyoming' }
];

function Checkout() {
  const { cartItems, cartSubtotal, clearCart } = useCart();
  const { currentUser } = useAuth();
  const navigate = useNavigate();

  // Shipping fields
  const [name, setName] = useState('');
  const [address, setAddress] = useState('');
  const [city, setCity] = useState('');
  const [state, setState] = useState('');
  const [zip, setZip] = useState('');

  // Dummy payment fields (no real processing — demo only)
  const [cardNumber, setCardNumber] = useState('');
  const [expiry, setExpiry] = useState('');
  const [cvv, setCvv] = useState('');

  // Discount code
  const [discountCode, setDiscountCode] = useState('');
  const [discountData, setDiscountData] = useState(null);
  const [discountError, setDiscountError] = useState('');
  const [checkingCode, setCheckingCode] = useState(false);

  const [submitting, setSubmitting] = useState(false);
  const [orderError, setOrderError] = useState('');

  // Calculate discount
  const discountAmount = discountData
    ? discountData.type === 'percent'
      ? cartSubtotal * (discountData.amount / 100)
      : Math.min(discountData.amount, cartSubtotal)
    : 0;

  const subtotalAfterDiscount = cartSubtotal - discountAmount;
  const taxAmount = subtotalAfterDiscount * TAX_RATE;
  const orderTotal = subtotalAfterDiscount + taxAmount;

  // Validate discount code against Firestore
  async function handleApplyDiscount() {
    if (!discountCode.trim()) return;
    setDiscountError('');
    setDiscountData(null);
    setCheckingCode(true);

    try {
      const codeRef = doc(db, 'discounts', discountCode.trim().toUpperCase());
      const codeSnap = await getDoc(codeRef);

      if (codeSnap.exists()) {
        const data = codeSnap.data();
        if (data.active) {
          setDiscountData({ type: data.type, amount: data.amount });
        } else {
          setDiscountError('This code is no longer active.');
        }
      } else {
        setDiscountError('Code not found. Please check and try again.');
      }
    } catch (err) {
      setDiscountError('Error checking code. Try again.');
      console.error(err);
    }

    setCheckingCode(false);
  }

  // Submit order to Firestore
  async function handleSubmit(e) {
    e.preventDefault();
    setOrderError('');
    setSubmitting(true);

    try {
      const orderData = {
        userId: currentUser.uid,
        userEmail: currentUser.email,
        items: cartItems,
        shipping: { name, address, city, state, zip },
        subtotal: cartSubtotal,
        discountCode: discountData ? discountCode.trim().toUpperCase() : null,
        discountAmount: discountAmount,
        tax: taxAmount,
        total: orderTotal,
        status: 'pending',
        createdAt: Timestamp.now()
      };

      const orderRef = await addDoc(collection(db, 'orders'), orderData);

      // Decrement stock for each purchased item
      for (const item of cartItems) {
        try {
          const productRef = doc(db, 'products', item.id);
          if (item.hasSize && item.selectedSize) {
            // Per-size stock: decrement only the bought size
            await updateDoc(productRef, {
              [`stock.${item.selectedSize}`]: increment(-item.quantity)
            });
          } else if (!item.hasSize) {
            // Flat stock: decrement the single number
            await updateDoc(productRef, {
              stock: increment(-item.quantity)
            });
          }
        } catch (err) {
          // Non-fatal — order is already placed, log and continue
          console.error(`Stock update failed for ${item.name}:`, err);
        }
      }

      clearCart();
      navigate('/order-confirmation', {
        state: { orderId: orderRef.id, total: orderTotal }
      });
    } catch (err) {
      setOrderError('Failed to place order. Please try again.');
      console.error(err);
    }

    setSubmitting(false);
  }

  const inputStyle = { borderRadius: '2px' };

  return (
    <div className="container py-5">
      <h4
        className="fw-bold text-uppercase mb-4"
        style={{ letterSpacing: '0.08em' }}
      >
        Checkout
      </h4>

      {orderError && <div className="alert alert-danger py-2 small">{orderError}</div>}

      <form onSubmit={handleSubmit}>
        <div className="row g-4">

          {/* ----- Left: Shipping + Payment ----- */}
          <div className="col-lg-7">

            {/* Shipping */}
            <div className="card border-0 shadow-sm mb-4" style={{ borderRadius: '2px' }}>
              <div className="card-body p-4">
                <h6
                  className="fw-bold text-uppercase mb-3"
                  style={{ letterSpacing: '0.08em' }}
                >
                  Shipping
                </h6>

                <div className="mb-3">
                  <label className="form-label small fw-semibold">Full Name</label>
                  <input type="text" className="form-control" style={inputStyle}
                    placeholder="Your full name"
                    value={name} onChange={e => setName(e.target.value)} required />
                </div>

                <div className="mb-3">
                  <label className="form-label small fw-semibold">Street Address</label>
                  <input type="text" className="form-control" style={inputStyle}
                    placeholder="123 Main St"
                    value={address} onChange={e => setAddress(e.target.value)} required />
                </div>

                <div className="row g-2">
                  <div className="col-5">
                    <label className="form-label small fw-semibold">City</label>
                    <input type="text" className="form-control" style={inputStyle}
                      placeholder="Austin"
                      value={city} onChange={e => setCity(e.target.value)} required />
                  </div>
                  <div className="col-4">
                    <label className="form-label small fw-semibold">State</label>
                    <select
                      className="form-select"
                      style={inputStyle}
                      value={state}
                      onChange={e => setState(e.target.value)}
                      required
                    >
                      <option value="">— Select —</option>
                      {US_STATES.map(s => (
                        <option key={s.abbr} value={s.abbr}>
                          {s.abbr} — {s.name}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="col-3">
                    <label className="form-label small fw-semibold">ZIP</label>
                    <input type="text" className="form-control" style={inputStyle}
                      placeholder="78701" maxLength="5"
                      value={zip} onChange={e => setZip(e.target.value)} required />
                  </div>
                </div>
              </div>
            </div>

            {/* Payment */}
            <div className="card border-0 shadow-sm mb-4" style={{ borderRadius: '2px' }}>
              <div className="card-body p-4">
                <h6
                  className="fw-bold text-uppercase mb-1"
                  style={{ letterSpacing: '0.08em' }}
                >
                  Payment
                </h6>
                <p className="text-muted small mb-3">Demo only — no real charges are made.</p>

                <div className="mb-3">
                  <label className="form-label small fw-semibold">Card Number</label>
                  <input type="text" className="form-control" style={inputStyle}
                    placeholder="1234 5678 9012 3456" maxLength="19"
                    value={cardNumber} onChange={e => setCardNumber(e.target.value)} required />
                </div>

                <div className="row g-2">
                  <div className="col-6">
                    <label className="form-label small fw-semibold">Expiry</label>
                    <input type="text" className="form-control" style={inputStyle}
                      placeholder="MM/YY" maxLength="5"
                      value={expiry} onChange={e => setExpiry(e.target.value)} required />
                  </div>
                  <div className="col-6">
                    <label className="form-label small fw-semibold">CVV</label>
                    <input type="text" className="form-control" style={inputStyle}
                      placeholder="123" maxLength="4"
                      value={cvv} onChange={e => setCvv(e.target.value)} required />
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* ----- Right: Order Summary ----- */}
          <div className="col-lg-5">
            <div
              className="card border-0 shadow-sm sticky-top"
              style={{ top: '80px', borderRadius: '2px' }}
            >
              <div className="card-body p-4">
                <h6
                  className="fw-bold text-uppercase mb-3"
                  style={{ letterSpacing: '0.08em' }}
                >
                  Order Summary
                </h6>
                <hr />

                {/* Items */}
                {cartItems.map(item => (
                  <div key={item.cartKey} className="d-flex justify-content-between mb-2 small">
                    <span className="text-muted">
                      {item.name}
                      {item.selectedSize && (
                        <span className="ms-1 text-uppercase" style={{ color: '#c84b11' }}>
                          ({item.selectedSize})
                        </span>
                      )}
                      <span className="ms-1">×{item.quantity}</span>
                    </span>
                    <span>${(item.price * item.quantity).toFixed(2)}</span>
                  </div>
                ))}
                <hr />

                {/* Discount code */}
                <div className="mb-3">
                  <label className="form-label small fw-semibold">Discount Code</label>
                  <div className="input-group input-group-sm">
                    <input
                      type="text"
                      className="form-control text-uppercase"
                      style={inputStyle}
                      placeholder="e.g. PORK10"
                      value={discountCode}
                      onChange={e => setDiscountCode(e.target.value)}
                    />
                    <button
                      type="button"
                      className="btn btn-outline-secondary"
                      style={{ borderRadius: '0 2px 2px 0' }}
                      onClick={handleApplyDiscount}
                      disabled={checkingCode}
                    >
                      {checkingCode ? '...' : 'Apply'}
                    </button>
                  </div>
                  {discountError && (
                    <p className="small text-danger mt-1 mb-0">{discountError}</p>
                  )}
                  {discountData && (
                    <p className="small mt-1 mb-0" style={{ color: '#2e7d32' }}>
                      Applied — {discountData.type === 'percent'
                        ? `${discountData.amount}% off`
                        : `$${discountData.amount} off`}
                    </p>
                  )}
                </div>

                {/* Price breakdown */}
                <div className="d-flex justify-content-between small mb-1">
                  <span>Subtotal</span>
                  <span>${cartSubtotal.toFixed(2)}</span>
                </div>
                {discountData && (
                  <div className="d-flex justify-content-between small mb-1" style={{ color: '#2e7d32' }}>
                    <span>Discount</span>
                    <span>−${discountAmount.toFixed(2)}</span>
                  </div>
                )}
                <div className="d-flex justify-content-between small mb-1 text-muted">
                  <span>Tax (8.25%)</span>
                  <span>${taxAmount.toFixed(2)}</span>
                </div>
                <hr />
                <div className="d-flex justify-content-between fw-bold mb-3">
                  <span>Total</span>
                  <span>${orderTotal.toFixed(2)}</span>
                </div>

                <button
                  type="submit"
                  className="btn btn-piggy w-100"
                  disabled={submitting || cartItems.length === 0}
                >
                  {submitting ? 'Placing Order...' : 'Place Order'}
                </button>
              </div>
            </div>
          </div>

        </div>
      </form>
    </div>
  );
}

export default Checkout;
